import React, {FunctionComponent, useMemo} from "react";
import {KeyboardArrowLeftRounded} from "@mui/icons-material";
import {Box, Divider, IconButton, Tooltip, Typography} from "@mui/material";
import {Link, useRouteMatch} from "react-router-dom";
import {renderPanelSidebarMenuItems} from "../index";
import {SidebarContent, usePanel} from "../../../layout";
import {routes} from "../../../../../core/models/static/routes/app-routes";
import useAccessLevels from "../../../../hooks/use-access-levels";
import {useAppSelector} from "../../../../redux";
import {useTranslation} from "react-i18next";
import type {CommandSearchScope} from "../../../panel-command-search";
import {createCustomerPanelEntries, panelEntriesToSidebarItems} from "../../../../panel-entries";

const CUSTOMER_BACK_ENTRY_ID = 'customer-back-to-customers';

const PanelSidebarCustomerContent: FunctionComponent = () => {
    const {params} = useRouteMatch<{ customerId: string }>(routes.customer.base) ?? {};
    const operatorAccessLevels = useAccessLevels();
    const panel = usePanel();
    const customerInformation = useAppSelector(state => state.customerInformation);
    const {t} = useTranslation("components", {keyPrefix: 'panel-sections.sidebar.customer'});
    const {t: tCmd} = useTranslation("components", {keyPrefix: 'panel-sections.command-search.pages'});

    const customerId = useMemo(() => params?.customerId ?? '0', [params?.customerId]);

    const entries = useMemo(
        () => createCustomerPanelEntries({t, tCmd, packages: operatorAccessLevels.packages}),
        [t, tCmd, operatorAccessLevels.packages],
    );

    const scope = useMemo<CommandSearchScope>(() => ({
        panelId: 'main',
        stack: [{type: 'customer', id: customerId}],
    }), [customerId]);

    const canAccess = useMemo(() => (accessKey: string) => {
        const [pkg, flag] = accessKey.split('.');
        return Boolean((operatorAccessLevels.packages as unknown as Record<string, Record<string, unknown>>)?.[pkg]?.[flag]);
    }, [operatorAccessLevels.packages]);

    const items = useMemo(
        () => panelEntriesToSidebarItems(entries, scope, {canAccess, panelEntries: entries})
            .filter((item) => item.key !== CUSTOMER_BACK_ENTRY_ID),
        [entries, scope, canAccess],
    );

    const content = useMemo(() => renderPanelSidebarMenuItems(items), [items]);

    return (
        <>
            <SidebarContent>
                <Divider sx={{marginInlineEnd: 2}}/>
                <Box sx={{display: "flex", alignItems: 'center', gap: 1}}>
                    <Tooltip title={t(tt => tt.backToMainSidebar)}>
                        <IconButton
                            sx={{
                                marginBlock: 2,
                                marginInline: panel.sidebarCollapsed ? 1 : 0,
                                '&:hover': {
                                    color: 'primary.main',
                                },
                            }}
                            component={Link as any}
                            to={routes.customers.list}
                            replace
                        >
                            <KeyboardArrowLeftRounded/>
                        </IconButton>
                    </Tooltip>
                    {
                        !panel.sidebarCollapsed &&
                        <Typography variant={'body2'} textOverflow={'ellipsis'} noWrap>
                            {t(t => t.title, {name: customerInformation?.name ?? "Customer"})}
                        </Typography>
                    }
                </Box>
                {content}
            </SidebarContent>
        </>
    );
}

export default PanelSidebarCustomerContent;
